import { Link } from "react-router-dom";
import { useRadioStore } from "../stores/radioStore";
import { Play, Pause, Volume2, VolumeX, X, Loader2, Shuffle, SkipBack, SkipForward, Film, Music } from "lucide-react";

const ARTWORK_PLACEHOLDER = "https://customer-assets.emergentagent.com/job_emt-task-check/artifacts/gaylzcdf_SP%20Logo%20Main.jpg";

export default function RadioPlayerBar() {
  const {
    playing, loading, visible, currentStation, currentTrack, currentPlan,
    volume, muted, shuffle,
    togglePlay, setVolume, toggleMute, stop, toggleShuffle, playNext, playPrev,
  } = useRadioStore();

  if (!visible) return null;

  const artwork = currentTrack?.artworkUrl || currentStation?.artworkUrl || currentStation?.logoURL || ARTWORK_PLACEHOLDER;
  const title = currentTrack?.title || "Now Playing";
  const artist = currentTrack?.artist || currentStation?.title || currentStation?.name || "StagePass Radio";
  const showTitle = currentTrack?.showTitle || null;

  // Determine badge and context
  const isContent = !!currentTrack?.contentId;
  const isVideo = currentTrack?.contentType === "video" || currentTrack?.contentType === "stream";
  const isAudio = currentTrack?.contentType === "audio";

  const badge = isContent
    ? isVideo
      ? { label: "VIDEO", icon: Film, color: "#00ff77" }
      : { label: "MUSIC", icon: Music, color: "#00ff77" }
    : { label: "LIVE", icon: null, color: "#B026FF" };

  return (
    <div
      data-testid="radio-player-bar"
      className="border-t border-white/10 flex-shrink-0"
      style={{ background: "linear-gradient(to right, #0d0d1a, #111)", minHeight: "72px" }}
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3 px-4 h-[72px]">
        {/* Artwork */}
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/10">
            <img
              src={artwork}
              alt={title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = ARTWORK_PLACEHOLDER; }}
            />
          </div>
          {/* Dynamic badge */}
          <span
            className="absolute -top-1 -right-1 flex items-center gap-0.5 text-[8px] font-bold text-white px-1 py-0.5 rounded-full"
            style={{ background: badge.color }}
          >
            {badge.icon ? <badge.icon size={7} /> : <span className="w-1 h-1 rounded-full bg-white animate-pulse" />}
            {badge.label}
          </span>
        </div>

        {/* Track info */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold truncate">{title}</p>
          <div className="flex items-center gap-2">
            <p className="text-zinc-400 text-xs truncate">{artist}</p>
            {showTitle && <span className="text-zinc-600 text-xs">· {showTitle}</span>}
          </div>
          {/* Link back to content OR to station */}
          {isContent && currentTrack?.contentId ? (
            <Link
              to={`/content/${currentTrack.contentId}`}
              className="text-[#00ff77] text-[10px] hover:underline truncate block"
            >
              Go back to {isVideo ? "video" : "track"}
            </Link>
          ) : currentStation ? (
            <Link
              to={`/radio/${currentStation.id}`}
              className="text-[#B026FF] text-[10px] hover:underline truncate block"
            >
              {currentStation.title || currentStation.name}
            </Link>
          ) : null}
        </div>

        {/* Controls - right side */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Volume on desktop */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={toggleMute}
              className="text-zinc-400 hover:text-white transition-colors"
              data-testid="radio-mute-btn"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={muted ? 0 : volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-20 h-1 accent-[#B026FF] cursor-pointer"
              data-testid="radio-volume"
            />
          </div>

          {/* Skip Back */}
          <button
            data-testid="radio-skip-prev"
            onClick={playPrev}
            disabled={!currentPlan?.segments?.length}
            title="Previous track"
            className="text-zinc-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <SkipBack size={17} />
          </button>

          {/* Play/Pause */}
          <button
            data-testid="radio-play-pause"
            onClick={togglePlay}
            disabled={loading}
            className="w-10 h-10 flex items-center justify-center rounded-full transition-all hover:scale-105 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #B026FF, #7C3AED)" }}
          >
            {loading ? (
              <Loader2 size={18} className="text-white animate-spin" />
            ) : playing ? (
              <Pause size={18} className="text-white" />
            ) : (
              <Play size={18} className="text-white ml-0.5" fill="white" />
            )}
          </button>

          {/* Skip Forward */}
          <button
            data-testid="radio-skip-next"
            onClick={playNext}
            disabled={!currentPlan?.segments?.length}
            title="Next track"
            className="text-zinc-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <SkipForward size={17} />
          </button>

          {/* Shuffle */}
          <button
            data-testid="radio-shuffle-btn"
            onClick={toggleShuffle}
            disabled={!currentPlan?.segments?.length}
            title={shuffle ? "Shuffle on" : "Shuffle off"}
            className={`transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
              shuffle ? "text-[#B026FF]" : "text-zinc-500 hover:text-white"
            }`}
          >
            <Shuffle size={15} />
          </button>

          {/* Stop/close */}
          <button
            data-testid="radio-stop-btn"
            onClick={stop}
            className="text-zinc-600 hover:text-white transition-colors p-1"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
